import React, { useCallback, useMemo, useState } from "react";
import {
    Stack,
    Title,
    Text,
    Table,
    TextInput,
    Button,
    Pagination,
    Group,
    ScrollArea,
    TableThead,
    TableTbody,
    TableTr,
    TableTh,
    TableTd,
    Select,
    rem,
    Modal,
    Alert,
    Loader,
    Center,
    Popover,
    Paper,
    Box,
    Badge,
    LoadingOverlay,
    Flex,
    UnstyledButton,
    ActionIcon,
    Switch
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdArrowDownward, MdArrowUpward, MdSearch, MdClose, MdFileDownload, MdCalendarToday, MdOutlineLightbulb, MdRefresh } from "react-icons/md";
import { useMsal } from "@azure/msal-react";
import { getToken, useLogin } from "../../authConfig";
import { getIdeasApi, exportIdeasApi } from "../../api";
import { IdeaItem, IdeaReviewStatus } from "../../api/models";
import { TimezoneDateDisplay } from "../common/TimezoneDateDisplay";
import { TimezoneUtils } from "../../utils/timezoneUtils";
import { LoadErrorAlert } from "../common/LoadErrorAlert";
import { useAbortableFetch } from "../../hooks/useAbortableFetch";
import { CATEGORY_COLORS, CATEGORY_LABELS, IDEA_REVIEW_STATUS_OPTIONS, IdeaReviewStatusBadge } from "./ideaLabels";

type ReviewStatusFilter = IdeaReviewStatus | "all";
const isValidReviewStatusFilter = (value: string | null): value is ReviewStatusFilter =>
    value === "all" || IDEA_REVIEW_STATUS_OPTIONS.some(option => option.value === value);

const IDEA_COLUMNS: Array<{
    key: keyof IdeaItem;
    label: string;
    width: number;
    flexible: boolean;
    sortable?: boolean;
}> = [
    { key: "review_status", label: "Status", width: 140, flexible: false, sortable: true },
    { key: "category", label: "Category", width: 200, flexible: false, sortable: true },
    { key: "idea", label: "Idea", width: 400, flexible: true, sortable: false },
    { key: "name", label: "User", width: 280, flexible: false, sortable: true },
    { key: "timestamp", label: "Submitted", width: 220, flexible: false, sortable: true }
];

export interface IdeaTableProps {
    userId?: string;
    title?: string;
    showExport?: boolean;
    showSearch?: boolean;
    showDateFilter?: boolean;
    showRefresh?: boolean;
    compact?: boolean;
    maxHeight?: string;
}

export const IdeaTable: React.FC<IdeaTableProps> = ({
    userId,
    title = "Ideas Inbox",
    showExport = true,
    showSearch = true,
    showDateFilter = true,
    showRefresh = true,
    compact = false,
    maxHeight = "60vh"
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState(() => (userId ? "" : searchParams.get("search")) || "");
    const [search, setSearch] = useState(() => (userId ? "" : searchParams.get("search")) || "");

    const [datePickerOpened, setDatePickerOpened] = useState(false);
    const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
    const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
        if (userId) return [null, null];
        const startDateParam = searchParams.get("startDate");
        const endDateParam = searchParams.get("endDate");
        if (startDateParam && endDateParam) {
            return [new Date(startDateParam), new Date(endDateParam)];
        }
        return [null, null];
    });

    const [categoryFilter, setCategoryFilter] = useState<string>(() => {
        if (userId) return "";
        return searchParams.get("category") || "";
    });

    const [includeDrafts, setIncludeDrafts] = useState<boolean>(() => {
        if (userId) return false;
        return searchParams.get("includeDrafts") === "true";
    });

    const [reviewStatusFilter, setReviewStatusFilter] = useState<ReviewStatusFilter>(() => {
        if (userId) return "all";
        const reviewStatusParam = searchParams.get("reviewStatus");
        return isValidReviewStatusFilter(reviewStatusParam) ? reviewStatusParam : "all";
    });

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [modalStartDate, setModalStartDate] = useState<string>("");
    const [modalEndDate, setModalEndDate] = useState<string>("");
    const [exportStatus, setExportStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [exportError, setExportError] = useState<string>("");

    const [sortBy, setSortBy] = useState<keyof IdeaItem>(() => (userId ? null : (searchParams.get("sortBy") as keyof IdeaItem)) || "timestamp");
    const [sortAsc, setSortAsc] = useState(() => {
        if (userId) return false;
        const sortAscParam = searchParams.get("sortAsc");
        return sortAscParam !== null ? sortAscParam === "true" : false;
    });
    const [page, setPage] = useState(() => (userId ? 0 : Number(searchParams.get("page"))) || 1);
    const [pageSize, setPageSize] = useState(() => (userId ? 0 : Number(searchParams.get("pageSize"))) || 15);

    const client = useLogin ? useMsal().instance : undefined;

    const updateSearchParams = (updates: Record<string, string | number | boolean>) => {
        if (userId) return;
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
        });
        setSearchParams(newParams);
    };

    const formatDateRange = () => {
        const [startDate, endDate] = dateRange;
        if (!startDate || !endDate) return null;
        const options: Intl.DateTimeFormatOptions = {
            month: "short",
            day: "numeric",
            year: startDate.getFullYear() !== endDate.getFullYear() ? "numeric" : undefined
        };
        return `${startDate.toLocaleDateString("en-US", options)} - ${endDate.toLocaleDateString("en-US", options)}`;
    };

    const applyDateRange = () => {
        if (tempStartDate && tempEndDate) {
            const startOfDay = new Date(tempStartDate);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(tempEndDate);
            endOfDay.setHours(23, 59, 59, 999);
            setDateRange([startOfDay, endOfDay]);
            setDatePickerOpened(false);
            updateSearchParams({
                startDate: startOfDay.toISOString(),
                endDate: endOfDay.toISOString()
            });
        }
    };

    const clearDateRange = () => {
        setDateRange([null, null]);
        setTempStartDate(null);
        setTempEndDate(null);
        setDatePickerOpened(false);
        updateSearchParams({ startDate: "", endDate: "" });
    };

    const fetchIdeas = useCallback(
        async (signal: AbortSignal) => {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available for fetching ideas");

            const [startDate, endDate] = dateRange;
            const startDateISO = startDate ? TimezoneUtils.formatDateForAPI(startDate) : undefined;
            const endDateISO = endDate ? TimezoneUtils.formatDateForAPI(endDate) : undefined;

            return getIdeasApi(
                idToken,
                page,
                pageSize,
                sortBy,
                sortAsc ? "asc" : "desc",
                search,
                startDateISO,
                endDateISO,
                userId,
                TimezoneUtils.getUserTimezone(),
                categoryFilter || undefined,
                includeDrafts,
                reviewStatusFilter,
                signal
            );
        },
        [client, page, pageSize, sortBy, sortAsc, search, dateRange, userId, categoryFilter, includeDrafts, reviewStatusFilter]
    );

    const { data, loading, error, reload: fetchData } = useAbortableFetch(fetchIdeas, "Failed to fetch ideas");
    const ideas = useMemo<IdeaItem[]>(() => data?.ideas || [], [data]);
    const totalPages = data?.total_pages || 1;
    const totalItems = data?.total_items || 0;

    const handleSort = (column: keyof IdeaItem) => {
        if (loading) return;
        const newSortAsc = sortBy === column ? !sortAsc : true;
        setSortBy(column);
        setSortAsc(newSortAsc);
        updateSearchParams({ sortBy: column, sortAsc: newSortAsc });
    };

    const applySearch = () => {
        const newSearch = searchInput.trim().toLowerCase();
        setSearch(newSearch);
        setPage(1);
        updateSearchParams({ search: newSearch, page: 1 });
    };

    const clearSearch = () => {
        setSearchInput("");
        setSearch("");
        setPage(1);
        updateSearchParams({ search: "", page: 1 });
    };

    const handleRefresh = () => {
        fetchData();
    };

    const openIdeaDetails = (ideaId: string) => {
        if (!loading) {
            navigate(`/administration/ideas/${encodeURIComponent(ideaId)}?${searchParams.toString()}`);
        }
    };

    const openExportModal = () => {
        setExportModalOpen(true);
        setExportStatus("idle");
        setExportError("");
        setModalStartDate("");
        setModalEndDate("");
    };

    const closeExportModal = () => {
        if (exportStatus === "loading") return;
        setExportModalOpen(false);
        setExportStatus("idle");
        setExportError("");
        setModalStartDate("");
        setModalEndDate("");
    };

    const handleModalExport = async () => {
        try {
            setExportStatus("loading");
            setExportError("");

            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available for exporting ideas");

            const startDateStr = modalStartDate || undefined;
            const endDateStr = modalEndDate || undefined;

            const blob = await exportIdeasApi(
                idToken,
                sortBy,
                sortAsc ? "asc" : "desc",
                search || undefined,
                startDateStr,
                endDateStr,
                userId,
                TimezoneUtils.getUserTimezone(),
                reviewStatusFilter
            );

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "ideas_export.xlsx";
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setExportStatus("success");
        } catch (error) {
            console.error("Export failed", error);
            setExportError("Export failed. Please try again.");
            setExportStatus("error");
        }
    };

    const visibleColumns = compact ? IDEA_COLUMNS.filter(c => ["review_status", "category", "idea", "timestamp"].includes(c.key)) : IDEA_COLUMNS;

    const renderCellValue = (idea: IdeaItem, key: keyof IdeaItem) => {
        switch (key) {
            case "review_status":
                return idea.status === "confirmed" ? <IdeaReviewStatusBadge status={idea.review_status} /> : null;
            case "category":
                return (
                    <Group gap={4} wrap="nowrap">
                        <Badge color={CATEGORY_COLORS[idea.category] || "gray"} variant="light" size="sm">
                            {CATEGORY_LABELS[idea.category] || idea.category}
                        </Badge>
                        {idea.status === "draft" && (
                            <Badge color="yellow" variant="outline" size="sm">
                                Draft
                            </Badge>
                        )}
                    </Group>
                );
            case "idea":
                return (
                    <Text size="sm" lineClamp={2} title={idea.idea}>
                        {idea.idea}
                    </Text>
                );
            case "name":
                return (
                    <Stack gap={2} maw={260}>
                        <Text size="sm" fw={600} lineClamp={1}>
                            {idea.name || "Unknown user"}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                            {idea.username}
                        </Text>
                    </Stack>
                );
            case "timestamp":
                return <TimezoneDateDisplay timestamp={idea.timestamp} showTime={true} showTimezone={false} size="sm" />;
            default:
                return null;
        }
    };

    return (
        <Stack gap="md" pos="relative" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <LoadingOverlay visible={loading && ideas.length > 0} />

            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
                <Stack gap={4}>
                    <Group gap="xs" align="center">
                        <Title order={4}>{title}</Title>
                        {!loading && (
                            <Text size="sm" c="dimmed">
                                ({totalItems.toLocaleString()} {totalItems === 1 ? "idea" : "ideas"})
                            </Text>
                        )}
                        {loading && ideas.length === 0 && <Loader size="xs" />}
                    </Group>
                </Stack>

                <Group gap="xs" wrap="wrap">
                    {showSearch && (
                        <TextInput
                            placeholder="Search ideas, category, user…"
                            value={searchInput}
                            onChange={e => setSearchInput(e.currentTarget.value)}
                            onKeyDown={e => e.key === "Enter" && applySearch()}
                            leftSection={<MdSearch size={16} />}
                            rightSection={
                                searchInput ? (
                                    <ActionIcon variant="subtle" size="sm" onClick={clearSearch}>
                                        <MdClose size={14} />
                                    </ActionIcon>
                                ) : null
                            }
                            size="sm"
                            w={240}
                        />
                    )}

                    <Select
                        placeholder="All categories"
                        value={categoryFilter || null}
                        onChange={val => {
                            const newVal = val || "";
                            setCategoryFilter(newVal);
                            setPage(1);
                            updateSearchParams({ category: newVal, page: 1 });
                        }}
                        data={[{ value: "", label: "All categories" }, ...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))]}
                        size="sm"
                        w={200}
                        clearable
                    />

                    <Select
                        value={reviewStatusFilter}
                        onChange={val => {
                            const newVal: ReviewStatusFilter = isValidReviewStatusFilter(val) ? val : "all";
                            setReviewStatusFilter(newVal);
                            setPage(1);
                            updateSearchParams({ reviewStatus: newVal === "all" ? "" : newVal, page: 1 });
                        }}
                        data={[{ value: "all", label: "All statuses" }, ...IDEA_REVIEW_STATUS_OPTIONS]}
                        size="sm"
                        w={150}
                        disabled={loading}
                    />

                    <Switch
                        label="Show drafts"
                        size="sm"
                        checked={includeDrafts}
                        onChange={e => {
                            const checked = e.currentTarget.checked;
                            setIncludeDrafts(checked);
                            setPage(1);
                            updateSearchParams({ includeDrafts: checked, page: 1 });
                        }}
                    />

                    {showDateFilter && (
                        <Popover opened={datePickerOpened} onClose={() => setDatePickerOpened(false)} position="bottom-end" withArrow shadow="md" width={320}>
                            <Popover.Target>
                                <Button
                                    variant={dateRange[0] && dateRange[1] ? "filled" : "default"}
                                    size="sm"
                                    leftSection={<MdCalendarToday size={16} />}
                                    rightSection={
                                        dateRange[0] && dateRange[1] ? (
                                            <ActionIcon
                                                variant="subtle"
                                                size="xs"
                                                color="white"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    clearDateRange();
                                                }}
                                            >
                                                <MdClose size={12} />
                                            </ActionIcon>
                                        ) : null
                                    }
                                    onClick={() => setDatePickerOpened(o => !o)}
                                >
                                    {formatDateRange() || "Date range"}
                                </Button>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Stack gap="sm">
                                    <DatePickerInput
                                        label="Start date"
                                        size="sm"
                                        value={tempStartDate}
                                        maxDate={tempEndDate || new Date()}
                                        onChange={date => {
                                            if (date) {
                                                setTempStartDate(typeof date === "string" ? new Date(date) : date);
                                            } else {
                                                setTempStartDate(null);
                                            }
                                        }}
                                        clearable={false}
                                    />
                                    <DatePickerInput
                                        label="End date"
                                        size="sm"
                                        value={tempEndDate}
                                        minDate={tempStartDate || undefined}
                                        maxDate={new Date()}
                                        onChange={date => {
                                            if (date) {
                                                setTempEndDate(typeof date === "string" ? new Date(date) : date);
                                            } else {
                                                setTempEndDate(null);
                                            }
                                        }}
                                        clearable={false}
                                    />
                                    <Group justify="flex-end" gap="xs">
                                        <Button variant="subtle" size="xs" onClick={() => setDatePickerOpened(false)}>
                                            Cancel
                                        </Button>
                                        <Button size="xs" onClick={applyDateRange} disabled={!tempStartDate || !tempEndDate}>
                                            Apply
                                        </Button>
                                    </Group>
                                </Stack>
                            </Popover.Dropdown>
                        </Popover>
                    )}

                    {showRefresh && (
                        <ActionIcon variant="default" size="lg" onClick={handleRefresh} disabled={loading} title="Refresh">
                            <MdRefresh size={18} />
                        </ActionIcon>
                    )}

                    {showExport && (
                        <Button variant="default" size="sm" leftSection={<MdFileDownload size={16} />} onClick={openExportModal} disabled={loading}>
                            Export
                        </Button>
                    )}
                </Group>
            </Group>

            <Paper withBorder radius="md" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {error ? (
                    <LoadErrorAlert title="Couldn't load ideas" error={error} onRetry={fetchData} />
                ) : ideas.length === 0 && !loading ? (
                    <Center h="100%" mih={compact ? 150 : 220}>
                        <Stack gap="xs" align="center">
                            <MdOutlineLightbulb size={36} color="var(--mantine-color-gray-5)" />
                            <Text fw={600}>No ideas found</Text>
                            <Text size="sm" c="dimmed" ta="center">
                                {userId
                                    ? "This user hasn't submitted any ideas yet."
                                    : "Try adjusting the search keywords, date range, category, or status filter."}
                            </Text>
                        </Stack>
                    </Center>
                ) : (
                    <ScrollArea.Autosize mah={maxHeight} type="auto">
                        <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md" style={{ fontSize: "0.9rem" }}>
                            <TableThead style={{ backgroundColor: "var(--mantine-color-gray-0)" }}>
                                <TableTr>
                                    {visibleColumns.map(column => (
                                        <TableTh key={column.label} style={{ minWidth: column.width }}>
                                            {column.sortable ? (
                                                <UnstyledButton
                                                    onClick={() => handleSort(column.key)}
                                                    style={{ display: "flex", alignItems: "center", gap: rem(4) }}
                                                >
                                                    <Text fw={600} size="sm">
                                                        {column.label}
                                                    </Text>
                                                    {sortBy === column.key ? sortAsc ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} /> : null}
                                                </UnstyledButton>
                                            ) : (
                                                <Text fw={600} size="sm">
                                                    {column.label}
                                                </Text>
                                            )}
                                        </TableTh>
                                    ))}
                                </TableTr>
                            </TableThead>
                            <TableTbody>
                                {ideas.map(idea => (
                                    <TableTr key={idea.id} onClick={() => openIdeaDetails(idea.id)} style={{ cursor: loading ? "default" : "pointer" }}>
                                        {visibleColumns.map(column => (
                                            <TableTd key={column.key}>{renderCellValue(idea, column.key)}</TableTd>
                                        ))}
                                    </TableTr>
                                ))}
                            </TableTbody>
                        </Table>
                    </ScrollArea.Autosize>
                )}
            </Paper>

            <Group justify="space-between" align="center">
                <Flex gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                        Rows per page
                    </Text>
                    <Select
                        value={String(pageSize)}
                        onChange={newPageSize => {
                            if (newPageSize) {
                                setPageSize(Number(newPageSize));
                                setPage(1);
                                updateSearchParams({ pageSize: newPageSize, page: 1 });
                            }
                        }}
                        data={[
                            { value: "10", label: "10 / page" },
                            { value: "15", label: "15 / page" },
                            { value: "25", label: "25 / page" },
                            { value: "50", label: "50 / page" },
                            { value: "100", label: "100 / page" }
                        ]}
                        w={150}
                        disabled={loading}
                    />
                    <Pagination
                        total={totalPages}
                        value={page}
                        onChange={newPage => {
                            setPage(newPage);
                            updateSearchParams({ page: newPage });
                        }}
                        disabled={loading}
                    />
                </Flex>
            </Group>

            {showExport && (
                <Modal
                    opened={exportModalOpen}
                    onClose={closeExportModal}
                    title="Export ideas"
                    size="md"
                    closeOnClickOutside={exportStatus !== "loading"}
                    closeOnEscape={exportStatus !== "loading"}
                >
                    <Stack gap="md">
                        {exportStatus === "success" ? (
                            <Alert color="green">Export downloaded successfully.</Alert>
                        ) : (
                            <>
                                <Text size="sm" c="dimmed">
                                    Optionally restrict the export to a date range. Leave blank to export all confirmed ideas matching the status filter.
                                </Text>
                                <Box>
                                    <Text size="sm" fw={500} mb={4}>
                                        Start date
                                    </Text>
                                    <TextInput
                                        type="date"
                                        value={modalStartDate}
                                        onChange={e => setModalStartDate(e.currentTarget.value)}
                                        disabled={exportStatus === "loading"}
                                        size="sm"
                                    />
                                </Box>
                                <Box>
                                    <Text size="sm" fw={500} mb={4}>
                                        End date
                                    </Text>
                                    <TextInput
                                        type="date"
                                        value={modalEndDate}
                                        onChange={e => setModalEndDate(e.currentTarget.value)}
                                        disabled={exportStatus === "loading"}
                                        size="sm"
                                    />
                                </Box>
                                {exportError && <Alert color="red">{exportError}</Alert>}
                                <Group justify="flex-end" gap="xs">
                                    <Button variant="subtle" onClick={closeExportModal} disabled={exportStatus === "loading"}>
                                        Cancel
                                    </Button>
                                    <Button
                                        leftSection={exportStatus === "loading" ? <Loader size="xs" color="white" /> : <MdFileDownload size={16} />}
                                        onClick={handleModalExport}
                                        loading={exportStatus === "loading"}
                                    >
                                        Download
                                    </Button>
                                </Group>
                            </>
                        )}
                    </Stack>
                </Modal>
            )}
        </Stack>
    );
};
